import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faHeart } from '@fortawesome/free-solid-svg-icons';
import { faGithub, faLinkedin, faXTwitter, IconDefinition } from '@fortawesome/free-brands-svg-icons';
import profile from '../profile.json';
import { Profile } from '../types/profile';

const typedProfile = profile as Profile;

interface SocialLink {
    icon: IconDefinition;
    url: string;
    label: string;
}

export default function Contact(): React.ReactElement {
    const { email } = typedProfile;
    const year = new Date().getFullYear();

    const socialLinks: SocialLink[] = [
        { icon: faGithub, url: typedProfile['social-media-links'].github, label: 'GitHub' },
        { icon: faLinkedin, url: typedProfile['social-media-links'].linkedin, label: 'LinkedIn' },
        { icon: faXTwitter, url: typedProfile['social-media-links'].twitter, label: 'X / Twitter' },
    ];

    return (
        <footer id='contact'>
            <section className="py-24 lg:py-32">
                <div className="container-main text-center max-w-2xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                        <div className="section-label justify-center">
                            <span>Contact</span>
                        </div>
                        <h2 className="section-title mb-6">Let's work together</h2>
                        <p className="text-slate-700 dark:text-dark-300 text-lg md:text-xl leading-relaxed mb-10">
                            I'm currently open to new opportunities. Whether you have a project in mind, 
                            want to collaborate, or just want to say hello — my inbox is always open.
                        </p>
                        <motion.a
                            href={`mailto:${email}`}
                            className="btn-primary inline-flex text-lg"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <FontAwesomeIcon icon={faEnvelope} />
                            <span>Say Hello</span>
                        </motion.a>
                    </motion.div>
                </div>
            </section>

            <div className="border-t border-slate-200/70 dark:border-white/5 py-10">
                <div className="container-main">
                    <div className="flex justify-center gap-4 mb-8">
                        {socialLinks.map(({ icon, url, label }) => (
                            <motion.a
                                key={label}
                                href={url}
                                target='_blank'
                                rel="noreferrer"
                                className="social-icon"
                                whileHover={{ y: -4 }}
                                aria-label={label}
                            >
                                <FontAwesomeIcon icon={icon} />
                            </motion.a>
                        ))}
                        <motion.a
                            href={`mailto:${email}`}
                            className="social-icon"
                            whileHover={{ y: -4 }}
                            aria-label="Email"
                        >
                            <FontAwesomeIcon icon={faEnvelope} />
                        </motion.a>
                    </div>

                    <div className="text-center">
                        <p className="text-slate-500 dark:text-dark-400 font-mono text-sm mb-2">
                            Designed & Built by{' '}
                            <a 
                                href="https://github.com/gogouravr"
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary-500 hover:text-primary-400 transition-colors"
                            >
                                Gourav Raj
                            </a>
                        </p>
                        <p className="text-slate-400 dark:text-dark-500 font-mono text-xs flex items-center justify-center gap-2">
                            Made with <FontAwesomeIcon icon={faHeart} className="text-accent-500" /> © {year}
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
